/**
 * Photo Settings Migration
 *
 * Extracted from customer_portal useMigratePhotoSettings.tsx.
 * Pure function to migrate legacy photo format/type fields to new photoFormatSettings structure.
 *
 * @packageDocumentation
 */

import { PhotoFormatSettings_PhotoFormat } from "@skydio/pbtypes/pbtypes/vehicle/skills/mission_camera_settings";

/**
 * Minimal interface for the photo settings fields needed by migration.
 * Matches the shape from Action.args.takePhoto / Action.args.togglePhotoIntervalMode.
 */
export interface MigratablePhotoAction {
  actionKey: "TakePhoto" | "TogglePhotoIntervalMode";
  args: {
    takePhoto?: {
      photoSettings?: {
        photoFormatSettings?: {
          eoPhotoFormats?: PhotoFormatSettings_PhotoFormat[];
          irPhotoFormats?: PhotoFormatSettings_PhotoFormat[];
        };
        photoFormat?: {
          format?: string;
        };
        photoType?: {
          irPhoto?: boolean;
        };
      };
    };
    togglePhotoIntervalMode?: {
      photoSettings?: {
        photoFormatSettings?: {
          eoPhotoFormats?: PhotoFormatSettings_PhotoFormat[];
          irPhotoFormats?: PhotoFormatSettings_PhotoFormat[];
        };
        photoFormat?: {
          format?: string;
        };
        photoType?: {
          irPhoto?: boolean;
        };
      };
    };
  };
}

export interface MigratedPhotoFormatSettings {
  eoPhotoFormats: PhotoFormatSettings_PhotoFormat[];
  irPhotoFormats: PhotoFormatSettings_PhotoFormat[];
}

/**
 * Migrate legacy photoFormat/photoType fields to new photoFormatSettings structure.
 * Returns undefined if no migration is needed (already has photoFormatSettings or no photoSettings).
 */
export function migratePhotoSettings(
  action: MigratablePhotoAction
): MigratedPhotoFormatSettings | undefined {
  const isTakePhoto = action.actionKey === "TakePhoto";
  const baseArgs = isTakePhoto ? action.args.takePhoto : action.args.togglePhotoIntervalMode;
  if (!baseArgs?.photoSettings || baseArgs.photoSettings.photoFormatSettings) {
    return;
  }
  const newPhotoFormatSettings: MigratedPhotoFormatSettings = {
    eoPhotoFormats: [PhotoFormatSettings_PhotoFormat.JPEG],
    irPhotoFormats: [],
  };
  if (baseArgs.photoSettings?.photoFormat?.format === "BOTH") {
    newPhotoFormatSettings.eoPhotoFormats.push(PhotoFormatSettings_PhotoFormat.DNG);
  }
  if (baseArgs.photoSettings?.photoType?.irPhoto) {
    newPhotoFormatSettings.irPhotoFormats.push(PhotoFormatSettings_PhotoFormat.JPEG);
  }
  return newPhotoFormatSettings;
}

/**
 * Get the base args path for a photo action (for use with updateActionPath).
 */
export function getPhotoSettingsBasePath(
  actionKey: "TakePhoto" | "TogglePhotoIntervalMode"
): string {
  return actionKey === "TakePhoto" ? "args.takePhoto" : "args.togglePhotoIntervalMode";
}
